import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ClipboardList,
  Eye,
  Mail,
  Monitor,
  Pencil,
  Search,
  Trash2,
  UserPlus,
  Users,
  SquarePen,
  GraduationCap,
  Upload,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import InputCst from "@components/InputCst/InputCst";
import SortMenuCst from "@components/SortMenuCst";
import PaginatorCst from "@components/PaginatorCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import {
  PERMISSIONS,
  PermissionGate,
  editionPerm,
  usePermissions,
} from "@utils/permissions";
import { ServicesURL, downloadFile, uploadFile } from "@utils/utilsApi";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import UploadModalCst from "@components/UploadModalCst";
import TeamsLimitAlertCst from "@components/TeamsLimitAlertCst";
import { openToast } from "@utils/toastStore";
import { ListItemAction } from "@components/ListItemCardCst/types";
import { openEditionEnrollmentModal } from "./EditionEnrollment/host";
import { useActivityInfo } from "./hooks/useActivityInfo";
import { useEditionsList } from "./hooks/useEditionsList";
import { useDeleteEdition } from "./hooks/useDeleteEdition";
import { useEditionMailto } from "./hooks/useEditionMailto";
import EditionCard from "./components/EditionCard";
import { EditionItem, EditionKind } from "./types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import PageHeaderV2Cst from "@components/PageHeaderV2Cst";

const PAGE_SIZE = 20;
const ENROLLMENT_UPLOAD_ACCEPT =
  "text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";
const ENROLLMENT_UPLOAD_MAX_RECORDS = 1000;
// Ricerca disabilitata finche' l'API di ricerca non e' pronta: per ora si usano
// i filtri canonici (accordion). Rimettere a true per riattivare la barra.
const EDITIONS_SEARCH_ENABLED = false;

const EditionsListPage: React.FC = () => {
  const { t } = useTranslation("editions");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", idLO = "" } = useParams();
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading, mutate } = useEditionsList({
    corporateId,
    objectId: Number(idLO),
    page,
    pageSize,
    search: search || undefined,
    sort: sort || undefined,
    fieldFilters,
  });

  const { activityTitle } = useActivityInfo(corporateId, idLO);

  const breadcrumbLabels = useMemo(
    () => (activityTitle ? { activities: activityTitle } : undefined),
    [activityTitle],
  );

  const kindParam = searchParams.get("kind");
  const kind: EditionKind =
    kindParam === "physical"
      ? "physical"
      : kindParam === "virtual"
        ? "virtual"
        : items[0]?.edition === "PHYSICAL"
          ? "physical"
          : "virtual";

  const { dataDetail: nonDeletableIds } = useFetchDataDetail(
    ServicesURL.getEditionsNotDeletable,
  );
  const nonDeletableSet = useMemo<Set<number | string>>(
    () => new Set(Array.isArray(nonDeletableIds) ? nonDeletableIds : []),
    [nonDeletableIds],
  );

  const { can, canAny } = usePermissions();
  // L'elenco richiede il permesso di lettura del tipo di edizione
  // (editions.physical.retrieve / editions.virtual.retrieve), come la card
  // edizioni di V1. Il tipo arriva da ?kind= oppure, se assente, dalle
  // edizioni caricate: finché non è noto non si blocca.
  const isKindResolved =
    kindParam === "physical" || kindParam === "virtual" || !isLoading;
  const canViewList = !isKindResolved || can(editionPerm(kind, "retrieve"));
  const { deleteEdition, isDeleting } = useDeleteEdition(kind);
  const { sendMail, isPreparing: isPreparingMail } = useEditionMailto({
    corporateId,
  });

  const [editionToDelete, setEditionToDelete] = useState<EditionItem | null>(
    null,
  );
  const [uploadOpen, setUploadOpen] = useState(false);

  const enrollmentQuery = `corporateId=${corporateId}&learningObjectId=${idLO}`;

  const handleDownloadEnrolled = () =>
    downloadFile(`${ServicesURL.getEnrollmentsDownload}?${enrollmentQuery}`);

  const handleDownloadTemplate = () =>
    downloadFile(ServicesURL.getEnrollmentsDownloadTemplate);

  const handleUploadEnrollments = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return uploadFile(
      `${ServicesURL.postEnrollmentsUpload}?${enrollmentQuery}`,
      formData,
    );
  };

  const sortOptions = useMemo(
    () => [
      { value: "most-recent", label: String(t("sort.most-recent")) },
      { value: "least-recent", label: String(t("sort.least-recent")) },
    ],
    [t],
  );

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSearch(searchInput.trim());
      setPage(1);
    }
  };

  const editionKind = (edition: EditionItem): EditionKind =>
    edition.edition === "VIRTUAL" ? "virtual" : "physical";

  const goToDetail = (edition: EditionItem, tab?: string) => {
    const k = editionKind(edition);
    const tabQS = tab ? `&tab=${tab}` : "";
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/activities/${idLO}/editions/${edition.id}?kind=${k}${tabQS}`,
    );
  };

  const handleManage = (edition: EditionItem) => goToDetail(edition);

  const handleCreate = () => {
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/activities/${idLO}/editions/add?kind=${kind}`,
    );
  };

  const handleEnroll = (edition: EditionItem) => {
    const objectId = edition.learning_objects?.id ?? Number(idLO);
    openEditionEnrollmentModal({
      editionId: edition.id,
      objectId,
      corporateId,
      objectTitle:
        edition.learning_objects?.label || edition.edition_name || undefined,
      onSuccess: () => mutate(),
    });
  };

  const handleSendEmail = async (edition: EditionItem) => {
    const outcome = await sendMail({
      editionId: edition.id,
      kind: editionKind(edition),
    });
    if (outcome === "URL_TOO_LONG") {
      openToast({
        severity: "warning",
        message: String(t("menu.mail.url-too-long")),
      });
    } else if (outcome === "ERROR") {
      openToast({
        severity: "error",
        message: String(t("menu.mail.error")),
      });
    }
  };

  const handleEdit = (edition: EditionItem) => {
    const k = editionKind(edition);
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/activities/${idLO}/editions/${edition.id}/edit?kind=${k}`,
    );
  };

  const handleConfirmDelete = async () => {
    if (!editionToDelete) return;
    try {
      await deleteEdition({
        editionId: editionToDelete.id,
        corporateId,
      });
      openToast({
        severity: "success",
        message: String(t("menu.delete.success")),
      });
      mutate();
    } catch {
      openToast({
        severity: "error",
        message: String(t("menu.delete.error")),
      });
    } finally {
      setEditionToDelete(null);
    }
  };

  const buildMenuActions = (
    edition: EditionItem,
  ): ListItemAction[][] | undefined => {
    const k = editionKind(edition);
    // D07: iscrizione con editions.*.enrollment (sezione Edizioni v1) OPPURE
    // editions.*.update (iscrizione a edizione dal Catalogo v1).
    const canEnroll = canAny([
      editionPerm(k, "enrollment"),
      editionPerm(k, "update"),
    ]);
    const canUpdate = can(editionPerm(k, "update"));
    // D08 (decisione: allineare V2 a v1): Invia email ai partecipanti con
    // learning.enrollment.read, come l'azione "mail" del config edizioni v1.
    const canSendMail = can(PERMISSIONS.enrollment.read);
    const canViewDetail = can(editionPerm(k, "retrieve"));
    const canViewEnrolledUsers =
      canViewDetail && can(PERMISSIONS.enrollment.read);
    const canViewRegistry = can(PERMISSIONS.registry.read);
    const isNotDeletable = nonDeletableSet.has(edition.id);

    // S05 (regola unica): tutte le voci del menu dell'edizione sono sempre
    // mostrate e vengono disabilitate se manca il permesso.
    return [
      [
        {
          label: String(t("menu.enroll")),
          icon: <UserPlus size={16} />,
          disabled: !canEnroll,
          onClick: () => handleEnroll(edition),
        },
        {
          label: String(t("menu.send-email")),
          icon: <Mail size={16} />,
          disabled: !canSendMail || isPreparingMail,
          onClick: () => handleSendEmail(edition),
        },
      ],
      [
        {
          label: String(t("menu.sessions")),
          icon: <Monitor size={16} />,
          disabled: !canViewDetail,
          onClick: () => goToDetail(edition, "sessions"),
        },
        {
          label: String(t("menu.registry")),
          icon: <ClipboardList size={16} />,
          disabled: !canViewDetail || !canViewRegistry,
          onClick: () => goToDetail(edition, "register"),
        },
        {
          label: String(t("menu.enrolled-users")),
          icon: <Users size={16} />,
          disabled: !canViewEnrolledUsers,
          onClick: () => goToDetail(edition, "enrolled-users"),
        },
      ],
      [
        {
          label: String(t(canUpdate ? "menu.edit" : "menu.view")),
          icon: canUpdate ? <Pencil size={16} /> : <Eye size={16} />,
          disabled: !canViewDetail,
          onClick: () => handleEdit(edition),
        },
        {
          label: String(t("menu.delete.label")),
          icon: <Trash2 size={16} />,
          disabled: !canUpdate || isNotDeletable,
          onClick: () => setEditionToDelete(edition),
        },
      ],
    ];
  };

  if (!canViewList) return <Navigate to="/403" replace />;

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <PageHeaderV2Cst>
          <Breadcrumb sticky={false} labelOverrides={breadcrumbLabels} />

          {!!idLO && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                mb: 1,
                color: theme.palette.exploreCardText,
              }}
            >
              <GraduationCap size={20} />
              {activityTitle && (
                <>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
                    {activityTitle}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderColor: theme.palette.systemSecondary01,
                      my: 0.25,
                    }}
                  />
                </>
              )}
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {t("activity-id", { id: idLO })}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {t("title")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <PermissionGate
                anyOf={[
                  editionPerm(kind, "enrollment"),
                  editionPerm(kind, "update"),
                ]}
                mode="disable"
              >
                <ButtonSecondary
                  size="md"
                  startIcon={<Upload size={16} />}
                  disabled={isLoading || items.length === 0}
                  onClick={() => setUploadOpen(true)}
                >
                  {t("upload-button")}
                </ButtonSecondary>
              </PermissionGate>
              <PermissionGate
                permission={editionPerm(kind, "create")}
                mode="disable"
              >
                <ButtonPrimary
                  size="md"
                  startIcon={<SquarePen size={16} />}
                  onClick={handleCreate}
                >
                  {t("create-button")}
                </ButtonPrimary>
              </PermissionGate>
            </Box>
          </Box>

          {isKindResolved && kind === "virtual" && (
            <Box sx={{ mb: 3 }}>
              <TeamsLimitAlertCst corporateId={corporateId} />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              flexWrap: "wrap",
            }}
          >
            {EDITIONS_SEARCH_ENABLED && (
              <Box
                sx={{
                  flex: "1 1 320px",
                  minWidth: 0,
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  "& .MuiFormHelperText-root": { display: "none" },
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
                onKeyDown={handleSearchKeyDown}
              >
                <InputCst
                  id="editions-search"
                  placeholder={String(t("search-placeholder"))}
                  inputSearch
                  value={searchInput}
                  onChange={(e: any) => setSearchInput(e.target.value)}
                  startAdornment={
                    <Search
                      size={16}
                      color={theme.palette.systemSecondary01}
                      style={{ marginRight: 8 }}
                    />
                  }
                />
              </Box>
            )}

            <FiltersAccordionToggle
              pageId={
                kind === "physical"
                  ? PagesID["corporate.physical-editions"]
                  : PagesID["corporate.virtual-editions"]
              }
              value={fieldFilters}
              onApply={(next) => {
                setFieldFilters(next);
                setPage(1);
              }}
              excludeKeys={["learning_objects_id"]}
            />

            <Box sx={{ flex: 1 }} />
            <SortMenuCst
              id="editions-sort"
              value={sort}
              options={sortOptions}
              onChange={(v) => setSort(v)}
            />
          </Box>
        </PageHeaderV2Cst>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.primary,
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {items.map((edition) => (
              <EditionCard
                key={edition.id}
                edition={edition}
                onManage={handleManage}
                menuActions={buildMenuActions(edition)}
              />
            ))}
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <ButtonSecondary size="md" onClick={() => navigate(-1)}>
            {t("back")}
          </ButtonSecondary>
        </Box>

        {editionToDelete && (
          <ConfirmDialogCst
            open
            title={String(t("menu.delete.title"))}
            description={String(
              t("menu.delete.description", {
                name:
                  editionToDelete.edition_name ||
                  editionToDelete.edition_code ||
                  `#${editionToDelete.id}`,
              }),
            )}
            confirmLabel={String(t("menu.delete.confirm"))}
            cancelLabel={String(t("menu.delete.cancel"))}
            confirmVariant="danger"
            isSubmitting={isDeleting}
            submittingLabel={String(t("menu.delete.deleting"))}
            onClose={() => setEditionToDelete(null)}
            onConfirm={handleConfirmDelete}
          />
        )}

        {uploadOpen && (
          <UploadModalCst
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            title={String(t("upload-modal.title"))}
            accept={ENROLLMENT_UPLOAD_ACCEPT}
            maxRecords={ENROLLMENT_UPLOAD_MAX_RECORDS}
            banner={{
              downloadLabel: String(t("upload-modal.download-template")),
              onDownload: handleDownloadTemplate,
              secondaryDownloadLabel: String(
                t("upload-modal.download-enrolled"),
              ),
              onSecondaryDownload: handleDownloadEnrolled,
              secondaryDownloadVariant: "contained",
            }}
            onUpload={handleUploadEnrollments}
            onUploaded={() => mutate()}
            successMessage={String(t("upload-modal.success"))}
          />
        )}
      </Box>
    </AnimationFadeIn>
  );
};

export default EditionsListPage;
